import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../index';

export interface Invitation {
  id: number;
  uuid: string;
  email: string;
  role_name: string;
  project_name: string;
  organization_name: string;
  invited_by_name: string;
  invited_by_email: string;
  invitation_url: string;
  email_invitation_url: string;
  created_at: string;
  accepted: number;
}

export interface InvitationStats {
  totalInvitations: number;
  pendingInvitations: number;
  acceptedInvitations: number;
  participantInvitations: number;
  teamMemberInvitations: number;
  guestInvitations: number;
}

export interface InvitationFilters {
  page: number;
  limit: number;
  search: string;
  status: string;
  roleType: string;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
}

export const invitationsApi = createApi({
  reducerPath: 'invitationsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Invitations', 'InvitationStats'],
  endpoints: (builder) => ({
    getInvitations: builder.query<
      {
        success: boolean;
        data: Invitation[];
        meta: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      },
      Partial<InvitationFilters>
    >({
      query: (params) => ({
        url: '/invitations',
        method: 'GET',
        params,
      }),
      providesTags: ['Invitations'],
    }),
    getInvitationStats: builder.query<
      {
        success: boolean;
        data: InvitationStats;
      },
      void
    >({
      query: () => '/invitations/stats',
      providesTags: ['InvitationStats'],
    }),
    deleteInvitation: builder.mutation<
      {
        success: boolean;
        message: string;
      },
      number
    >({
      query: (id) => ({
        url: `/invitations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invitations', 'InvitationStats'],
    }),
    resendInvitation: builder.mutation<
      {
        success: boolean;
        message: string;
      },
      number
    >({
      query: (id) => ({
        url: `/invitations/${id}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Invitations', 'InvitationStats'],
    }),
  }),
});

export const {
  useGetInvitationsQuery,
  useGetInvitationStatsQuery,
  useDeleteInvitationMutation,
  useResendInvitationMutation,
} = invitationsApi;

